import React, { useEffect, useMemo, useState } from 'react';
import api, { quotesAPI } from '../../services/api';
import { usePermissions } from '../../contexts/PermissionContext';
import { CountryCombobox } from '../ui/country-combobox';
import { formatCurrency } from '../../utils/formatCurrency';

// Change any part of a booking. Details (addresses, contents, references...)
// are open to anyone who can edit bookings; what the booking is charged on
// (destination, type, weight and size, insurance, carrier, price) is for
// admins, with a reason. The server decides both again, and settles a new
// price against what the customer has paid.

const SERVICE_TYPES = [['standard', 'Standard'], ['express', 'Express'], ['overnight', 'Overnight'],
  ['economy', 'Economy'], ['same_day', 'Same day']];
const SHIPMENT_TYPES = [['parcel', 'Parcel'], ['document', 'Document'], ['cargo', 'Cargo']];
const ADDRESS_FIELDS = [
  ['name', 'Name'], ['phone', 'Phone'], ['company', 'Company (optional)'], ['email', 'Email (optional)'],
  ['street', 'Address', true], ['landmark', 'Landmark (optional)'], ['city', 'City'],
  ['state', 'State (optional)'], ['postal_code', 'Postal code'],
];
const PACKAGE_NUMBERS = ['weight', 'length', 'width', 'height', 'declared_value', 'quantity'];
// Changing any of these needs an admin and a reason.
const CHARGED = {
  recipient: ['country'],
  package: ['type', 'weight', 'length', 'width', 'height'],
  top: ['insurance_required', 'insurance_amount', 'carrier_name', 'service_type', 'price'],
};
const TOP_NUMBERS = ['insurance_amount', 'price'];
const TOP_FIELDS = ['signature_required', 'insurance_required', 'insurance_amount', 'carrier_name', 'service_type',
  'awb', 'carrier_reference', 'custom_reference', 'pickup_date', 'notes', 'price'];

const str = (v) => (v === null || v === undefined ? '' : String(v));

const fromBooking = (d) => {
  const pkg = d.package_info || {};
  const dim = pkg.dimensions || {};
  const carrier = d.carrier_info || {};
  const address = (a) => Object.fromEntries(ADDRESS_FIELDS.map(([k]) => [k, str((a || {})[k])]));
  return {
    sender: address(d.sender),
    recipient: { ...address(d.recipient), country: str((d.recipient || {}).country) },
    package: {
      type: str(pkg.type || 'parcel'),
      weight: str(dim.weight), length: str(dim.length), width: str(dim.width), height: str(dim.height),
      contents_description: str(pkg.contents_description), declared_value: str(pkg.declared_value),
      quantity: str(pkg.quantity ?? 1), fragile: !!pkg.fragile, dangerous_goods: !!pkg.dangerous_goods,
    },
    signature_required: !!d.signature_required,
    insurance_required: !!d.insurance_required,
    insurance_amount: str(d.insurance_amount),
    carrier_name: str(carrier.carrier_name),
    service_type: str(carrier.service_type || 'standard'),
    awb: str(carrier.tracking_number),
    carrier_reference: str(carrier.carrier_reference),
    custom_reference: str(d.custom_reference),
    pickup_date: d.pickup_date ? String(d.pickup_date).slice(0, 10) : '',
    notes: str(d.notes),
    price: str(d.final_cost),
  };
};

const same = (a, b) => (typeof a === 'boolean' || typeof b === 'boolean' ? !!a === !!b : str(a).trim() === str(b).trim());
const value = (v, numeric) => (typeof v === 'boolean' ? v : numeric ? Number(String(v).trim()) : String(v).trim());

// Only what differs from the booking goes to the server.
const changedFields = (init, form) => {
  const body = {};
  const charged = [];
  for (const side of ['sender', 'recipient']) {
    for (const key of Object.keys(form[side])) {
      if (!same(init[side][key], form[side][key])) {
        body[side] = { ...(body[side] || {}), [key]: value(form[side][key]) };
        if (side === 'recipient' && CHARGED.recipient.includes(key)) charged.push(key);
      }
    }
  }
  for (const key of Object.keys(form.package)) {
    if (!same(init.package[key], form.package[key])) {
      body.package = { ...(body.package || {}), [key]: value(form.package[key], PACKAGE_NUMBERS.includes(key)) };
      if (CHARGED.package.includes(key)) charged.push(key);
    }
  }
  for (const key of TOP_FIELDS) {
    if (!same(init[key], form[key])) {
      body[key] = value(form[key], TOP_NUMBERS.includes(key));
      if (CHARGED.top.includes(key)) charged.push(key);
    }
  }
  return { body, charged };
};

const inputCls =
  'w-full border border-gray-300 rounded-md px-2.5 py-1.5 text-sm focus:outline-none focus:ring-1 '
  + 'focus:ring-orange-500 disabled:bg-gray-50 disabled:text-gray-400';

const Box = ({ title, note, children }) => (
  <div className="rounded-lg border border-gray-200 p-4">
    <div className="mb-3 flex items-baseline justify-between gap-2">
      <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-700">{title}</h4>
      {note && <span className="text-[11px] text-gray-400">{note}</span>}
    </div>
    {children}
  </div>
);

const Labelled = ({ label, wide, children }) => (
  <label className={`block ${wide ? 'col-span-2' : ''}`}>
    <span className="mb-1 block text-[11px] uppercase tracking-wide text-gray-400">{label}</span>
    {children}
  </label>
);

const Check = ({ label, checked, onChange, disabled, test }) => (
  <label className={`flex items-center gap-2 text-sm ${disabled ? 'text-gray-400' : 'text-gray-700'}`}>
    <input type="checkbox" data-test={test} checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
    {label}
  </label>
);

const BookingEditForm = ({ booking, onCancel, onSaved }) => {
  const { hasPermission } = usePermissions();
  const canCharge = hasPermission('override_booking_price');
  const init = useMemo(() => fromBooking(booking), [booking]);
  const [form, setForm] = useState(init);
  const [reason, setReason] = useState('');
  const [countries, setCountries] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get('/admin/booking-carriers').then((r) => setCarriers(r.data?.data || [])).catch(() => setCarriers([]));
    if (canCharge) {
      quotesAPI.getPriorityConnectionsCountries()
        .then((data) => setCountries(data?.countries || []))
        .catch(() => setCountries([]));
    }
  }, [canCharge]);

  const set = (path, v) => setForm((f) => {
    const [a, b] = path.split('.');
    return b ? { ...f, [a]: { ...f[a], [b]: v } } : { ...f, [a]: v };
  });

  const { body, charged } = changedFields(init, form);
  const nothingChanged = Object.keys(body).length === 0;

  // What a new price does to the money, before it is saved.
  const pay = booking.payment_info || {};
  const paid = Number(booking.paid_so_far || 0);
  const newPrice = Number(form.price);
  let priceEffect = null;
  if ('price' in body && Number.isFinite(newPrice) && newPrice > 0) {
    const diff = Math.round((newPrice - paid) * 100) / 100;
    if (pay.status === 'pending') priceEffect = `The customer will owe ₹${formatCurrency(newPrice)}.`;
    else if (diff > 0) priceEffect = `The customer has paid ₹${formatCurrency(paid)}; ₹${formatCurrency(diff)} will be owed.`;
    else if (diff < 0) priceEffect = `The customer has paid ₹${formatCurrency(paid)}; ₹${formatCurrency(-diff)} will be refunded to their wallet.`;
    else priceEffect = 'The customer has paid exactly this.';
  }

  const save = async () => {
    setError(null);
    if (nothingChanged) {
      setError('Nothing has been changed.');
      return;
    }
    if (charged.length && reason.trim().length < 3) {
      setError('Give a reason for changing the carrier, weight, destination, insurance or price.');
      return;
    }
    const payload = { ...body, reason: reason.trim() || undefined };
    if ('price' in body) {
      payload.expected_price = booking.final_cost;
      payload.expected_paid = booking.paid_so_far;
    }
    setSaving(true);
    try {
      const res = await api.patch(`/admin/bookings/${booking.id}`, payload);
      onSaved(res.data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail
        : Array.isArray(detail) && detail[0]?.msg ? detail[0].msg : 'The changes could not be saved. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const adminNote = canCharge ? null : 'Only admins can change the greyed-out fields';

  const addressBox = (side, title) => (
    <Box title={title}>
      <div className="grid grid-cols-2 gap-3">
        {ADDRESS_FIELDS.map(([key, label, wide]) => (
          <Labelled key={key} label={label} wide={wide}>
            <input data-test={`edit-${side}-${key}`} className={inputCls} value={form[side][key]}
              onChange={(e) => set(`${side}.${key}`, e.target.value)} />
          </Labelled>
        ))}
        {side === 'recipient' ? (
          <Labelled label="Country" wide>
            {canCharge && countries.length > 0 ? (
              <CountryCombobox id="edit-recipient-country" countries={countries} value={form.recipient.country}
                onChange={(code) => set('recipient.country', code)} placeholder="Destination" />
            ) : (
              <input className={inputCls} value={form.recipient.country} disabled />
            )}
          </Labelled>
        ) : (
          <Labelled label="Country" wide><input className={inputCls} value={form.sender.country || 'India'} disabled /></Labelled>
        )}
      </div>
    </Box>
  );

  return (
    <div data-test="edit-form" className="space-y-4">
      <div className="grid gap-4 md:grid-cols-2">
        {addressBox('sender', 'Sender')}
        {addressBox('recipient', 'Recipient (destination)')}
      </div>

      <Box title="Package & contents" note={adminNote}>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <Labelled label="Type">
            <select data-test="edit-type" className={inputCls} value={form.package.type} disabled={!canCharge}
              onChange={(e) => set('package.type', e.target.value)}>
              {SHIPMENT_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </Labelled>
          {[['weight', 'Weight (kg)'], ['length', 'Length (cm)'], ['width', 'Width (cm)'], ['height', 'Height (cm)']].map(([key, label]) => (
            <Labelled key={key} label={label}>
              <input data-test={`edit-${key}`} className={inputCls} inputMode="decimal" value={form.package[key]}
                disabled={!canCharge} onChange={(e) => set(`package.${key}`, e.target.value)} />
            </Labelled>
          ))}
          <Labelled label="Contents" wide>
            <input data-test="edit-contents" className={inputCls} value={form.package.contents_description}
              onChange={(e) => set('package.contents_description', e.target.value)} />
          </Labelled>
          <Labelled label="Declared value (₹)">
            <input data-test="edit-declared" className={inputCls} inputMode="decimal" value={form.package.declared_value}
              onChange={(e) => set('package.declared_value', e.target.value)} />
          </Labelled>
          <Labelled label="Quantity">
            <input className={inputCls} inputMode="numeric" value={form.package.quantity}
              onChange={(e) => set('package.quantity', e.target.value)} />
          </Labelled>
          <div className="col-span-2 flex flex-wrap gap-x-5 gap-y-2 pt-1 md:col-span-4">
            <Check label="Fragile" checked={form.package.fragile} onChange={(v) => set('package.fragile', v)} />
            <Check label="Dangerous goods" checked={form.package.dangerous_goods} onChange={(v) => set('package.dangerous_goods', v)} />
            <Check label="Signature required" checked={form.signature_required} onChange={(v) => set('signature_required', v)} />
            <Check label="Insurance" test="edit-insurance" checked={form.insurance_required} disabled={!canCharge}
              onChange={(v) => set('insurance_required', v)} />
          </div>
          {form.insurance_required && (
            <Labelled label="Insurance amount (₹)">
              <input className={inputCls} inputMode="decimal" value={form.insurance_amount} disabled={!canCharge}
                onChange={(e) => set('insurance_amount', e.target.value)} />
            </Labelled>
          )}
        </div>
      </Box>

      <Box title="Carrier, AWB & pickup" note={adminNote}>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
          <Labelled label="Carrier">
            <input data-test="edit-carrier" className={inputCls} list="booking-carrier-names" value={form.carrier_name}
              disabled={!canCharge} onChange={(e) => set('carrier_name', e.target.value)} />
            <datalist id="booking-carrier-names">
              {carriers.map((c) => <option key={c} value={c} />)}
            </datalist>
          </Labelled>
          <Labelled label="Service">
            <select className={inputCls} value={form.service_type} disabled={!canCharge}
              onChange={(e) => set('service_type', e.target.value)}>
              {SERVICE_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </Labelled>
          <Labelled label="AWB / tracking number">
            <input data-test="edit-awb" className={`${inputCls} font-mono`} value={form.awb}
              onChange={(e) => set('awb', e.target.value)} />
          </Labelled>
          <Labelled label="Carrier reference">
            <input className={inputCls} value={form.carrier_reference} onChange={(e) => set('carrier_reference', e.target.value)} />
          </Labelled>
          <Labelled label="Our reference">
            <input className={inputCls} value={form.custom_reference} onChange={(e) => set('custom_reference', e.target.value)} />
          </Labelled>
          <Labelled label="Pickup date">
            <input data-test="edit-pickup" type="date" className={inputCls} value={form.pickup_date}
              onChange={(e) => set('pickup_date', e.target.value)} />
          </Labelled>
        </div>
      </Box>

      <Box title="Price" note={canCharge ? null : 'Only admins can change the price'}>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
          <Labelled label="Price (₹)">
            <input data-test="edit-price" className={inputCls} inputMode="decimal" value={form.price} disabled={!canCharge}
              onChange={(e) => set('price', e.target.value)} />
          </Labelled>
          <div className="col-span-1 text-sm text-gray-600 md:col-span-2">
            <span className="mb-1 block text-[11px] uppercase tracking-wide text-gray-400">Paid so far</span>
            ₹{formatCurrency(paid)}
            {pay.payment_method === 'partial' && <span className="ml-1 text-xs text-gray-400">(pay at pickup)</span>}
          </div>
        </div>
        {priceEffect && (
          <p data-test="price-effect" className="mt-3 rounded-md bg-orange-50 px-3 py-2 text-xs text-orange-900">{priceEffect}</p>
        )}
      </Box>

      <Box title="Internal notes">
        <textarea className={inputCls} rows={2} value={form.notes} onChange={(e) => set('notes', e.target.value)} />
      </Box>

      <Labelled label={charged.length ? 'Reason (required, kept with the change)' : 'Reason (optional, kept with the change)'}>
        <textarea data-test="edit-reason" className={inputCls} rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
      </Labelled>

      {error && <p data-test="edit-error" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">{error}</p>}

      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} disabled={saving}
          className="px-4 py-2 bg-gray-100 text-gray-800 text-sm rounded-md hover:bg-gray-200 disabled:opacity-50">
          Cancel
        </button>
        <button type="button" data-test="edit-save" onClick={save} disabled={saving || nothingChanged}
          className="px-4 py-2 bg-orange-600 text-white text-sm rounded-md hover:bg-orange-700 disabled:opacity-50">
          {saving ? 'Saving...' : 'Save changes'}
        </button>
      </div>
    </div>
  );
};

export default BookingEditForm;
